import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, OnInit, TemplateRef, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  MatLegacyTableDataSource as MatTableDataSource,
  MatLegacyTableModule as MatTableModule,
} from '@angular/material/legacy-table';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TsButtonModule, TsSnackbarService } from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { TsTabDefinition, TsTabGroupComponent } from 'tsui/lib/ts-tab-group/ts-tab-group.component';
import { Actions, Store, ofActionCompleted, ofActionErrored } from '@ngxs/store';
import { Folders } from '../state/actions';
import { AccountState } from 'app/state/account/state';
import { UserState } from '@state/user/state';
import { NewPermission } from '@core/enums';
import { DirectivesModule } from '@shared/directives/directives.module';
import { catchError, filter, Observable, switchMap, tap, throwError } from 'rxjs';
import { Account } from 'app/core/models/account.types';
import { FileResource, Document } from '../folders.types';
import { DELETE_LAST_DOCUMENT_CONFIRMATION } from '../folders.constants';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { WidgetsModule } from 'app/shared/components/widgets/widgets.module';
import { PipesModule } from 'app/shared/pipes/pipes.module';
import { AccountFoldersService } from 'app/modules/folders/folders.service';

export interface FoldersTabViewFileDialogData {
  fileResource: FileResource;
  readonly?: boolean;
  accountScoped?: boolean;
  onDeleteAction?: (document: Document, fileDeleted?: boolean) => void;
}

@Component({
  selector: 'folders-tab-view-file',
  standalone: true,
  imports: [
    CommonModule,
    TsButtonModule,
    TsDialogComponent,
    MatIconModule,
    MatTableModule,
    TsTabGroupComponent,
    PipesModule,
    WidgetsModule,
    DirectivesModule,
  ],
  templateUrl: './view-file.component.html',
  styleUrls: ['./view-file.component.scss'],
})
export class FoldersTabViewFileComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  protected readonly NewPermission = NewPermission;
  static getDialogConfig(data: FoldersTabViewFileDialogData) {
    return {
      ...TsDialogComponent.getDefaultDialogConfig(),
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      maxHeight: '100%',
      data,
    };
  }

  private readonly data = inject<FoldersTabViewFileDialogData>(DIALOG_DATA);
  private readonly dialogRef = inject(DialogRef);
  private readonly accountFoldersService = inject(AccountFoldersService);
  private readonly route = inject(ActivatedRoute);

  localDocsTemplate = viewChild<TemplateRef<unknown>>('localDocsTemplate');
  integrationDocsTemplate = viewChild<TemplateRef<unknown>>('integrationDocsTemplate');

  editWopiFile = false;
  expandRightSidebar = true;
  selectedFile: FileResource = this.data.fileResource;
  selectedDocument: Document;
  localDocumentsDataSource = new MatTableDataSource<Document>();
  integrationDocumentsDataSource = new MatTableDataSource<Document>();
  localDocumentsColumns = ['version', 'assetable_category_label', 'created_by', 'created_at', 'actions'];
  integrationDocumentsColumns = ['version', 'integrated_storage', 'created_by', 'created_at', 'actions'];
  tabs: TsTabDefinition[];
  account: Account;
  readonly canEditFile =
    !this.isReadonly && this.store.selectSnapshot(UserState.hasPermission(NewPermission.FILES_EDIT));
  readonly canDeleteFile =
    !this.isReadonly && this.store.selectSnapshot(UserState.hasPermission(NewPermission.FILES_DELETE));

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private store: Store,
    private readonly actions$: Actions,
    private confirmService: ConfirmService,
    private readonly snackBar: TsSnackbarService,
  ) {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);
  }

  get isReadonly(): boolean {
    return !!this.data?.readonly;
  }

  get isAccountScoped(): boolean {
    return !!this.data?.accountScoped;
  }

  get previewPdfUrl(): string | undefined {
    const document = this.selectedDocument;
    const url = document?.pdf_url;

    if (!this.isInlinePreviewUrl(url, document?.original_url)) {
      return undefined;
    }

    const pageNum = this.route.snapshot.queryParams['pageNum'];

    if (pageNum && document.public_name?.toLowerCase().endsWith('.pdf')) {
      return `${url}#page=${pageNum}`;
    }

    return url;
  }

  ngOnInit(): void {
    if (this.isAccountScoped) {
      this.accountFoldersService.accountId = this.store.selectSnapshot(AccountState.getAccount)?.id;
    }

    // Newly uploaded files often only have `latest_document` until the next folder fetch.
    this.applyFileResource(this.selectedFile);
    this.loadFile();

    if (!this.isAccountScoped) {
      this.setupStateActions();
    }
  }

  ngAfterViewInit(): void {
    this.tabs = [
      { label: 'Documents', template: this.localDocsTemplate() },
      { label: 'Integration Documents', template: this.integrationDocsTemplate() },
    ];
    this._changeDetectorRef.detectChanges();
  }

  setSelectedDocument(document?: Document): void {
    if (document) {
      this.selectedDocument = document;
      return;
    }

    const [firstDocument] = this.resolveDocuments(this.selectedFile);

    if (firstDocument) {
      this.selectedDocument = firstDocument;
    }
  }

  toggleWopiFile(): void {
    this.editWopiFile = !this.editWopiFile;

    if (this.selectedFile?.wopi?.errors) {
      return;
    }

    if (this.editWopiFile) {
      this._changeDetectorRef.detectChanges();
      // TODO: This is probably not the best way to accomplish posting to the form.
      const form = window.document.getElementById('officeForm') as HTMLFormElement;

      if (form) {
        form.submit();
      }
    }
  }

  downloadDocument(document: Document): void {
    window.open(document.original_url, '_blank');
  }

  deleteDocument(document: Document): void {
    if (!this.canDeleteFile) {
      return;
    }

    const isLastDocument = (this.selectedFile.documents?.length ?? 0) <= 1;
    this.confirmService
      .openConfirmationModal(isLastDocument ? DELETE_LAST_DOCUMENT_CONFIRMATION : undefined)
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
        switchMap(() => this.deleteDocumentRequest(document, isLastDocument)),
      )
      .subscribe(() => {
        this.data.onDeleteAction?.(document, isLastDocument);

        if (isLastDocument) {
          this.discard();
        } else {
          this.loadFile();
        }
      });
  }

  deleteFile(): void {
    if (!this.canDeleteFile) {
      return;
    }

    this.confirmService
      .openConfirmationModal()
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
        switchMap(() => this.deleteFileRequest()),
      )
      .subscribe(() => {
        this.discard();
      });
  }

  onFileChanged(event: any) {
    if (!this.canEditFile) {
      return;
    }

    const document: File = event.target.files[0];
    this.createFileDocumentRequest(document)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        event.target.value = '';
        this.loadFile();
      });
  }

  openOnline(document) {
    window.open(document?.links?.edit?.link?.webUrl, '_blank');
  }

  discard(): void {
    this.dialogRef.close();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  private deleteDocumentRequest(document: Document, isLastDocument: boolean): Observable<unknown> {
    if (this.isAccountScoped) {
      return isLastDocument
        ? this.accountFoldersService.deleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id)
        : this.accountFoldersService.deleteDocument(
            this.selectedFile.folder_resource_id,
            this.selectedFile.id,
            document.id,
          );
    }

    return isLastDocument
      ? this.store.dispatch(new Folders.DeleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id))
      : this.store.dispatch(
          new Folders.DeleteDocument(this.selectedFile.folder_resource_id, this.selectedFile.id, document.id),
        );
  }

  private deleteFileRequest(): Observable<unknown> {
    if (this.isAccountScoped) {
      return this.accountFoldersService.deleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id);
    }

    return this.store.dispatch(new Folders.DeleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id));
  }

  private createFileDocumentRequest(document: File): Observable<unknown> {
    if (this.isAccountScoped) {
      return this.accountFoldersService.uploadDocument(
        this.selectedFile.folder_resource_id,
        this.selectedFile.id,
        document,
      );
    }

    return this.store.dispatch(
      new Folders.CreateFileDocument(this.selectedFile.folder_resource_id, this.selectedFile.id, document),
    );
  }

  private applyFileResource(fileResource: FileResource): void {
    this.localDocumentsDataSource.data = this.resolveDocuments(fileResource).sort((a, b) => (a.id < b.id ? 1 : -1));
    this.integrationDocumentsDataSource.data = (fileResource.integration_documents ?? []).sort((a, b) =>
      a.id < b.id ? 1 : -1,
    );
    this.selectedFile = fileResource;
    this.setSelectedDocument();
    this._changeDetectorRef.markForCheck();
  }

  private resolveDocuments(fileResource: FileResource): Document[] {
    if (fileResource.local_documents?.length) {
      return fileResource.local_documents;
    }

    if (fileResource.documents?.length) {
      return fileResource.documents;
    }

    return fileResource.latest_document ? [fileResource.latest_document] : [];
  }

  private isInlinePreviewUrl(url?: string, originalUrl?: string): boolean {
    if (!url) {
      return false;
    }

    const normalizedUrl = url.toLowerCase();

    if (normalizedUrl.includes('/download') || normalizedUrl.includes('content-disposition=attachment')) {
      return false;
    }

    return !originalUrl || url.split('#')[0] !== originalUrl.split('#')[0];
  }

  private setupStateActions(): void {
    // Fallback to when GetFileById endpoint fails.
    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionErrored(Folders.GetFileById),
        tap(() => {
          this.snackBar.open('Unable to open the file. Please try again later.', 'error');
          this.discard();
        }),
      )
      .subscribe();

    this.actions$
      .pipe(untilComponentDestroyed(this), ofActionCompleted(Folders.GetFileById))
      .subscribe(() => this.store.dispatch(new Folders.GetFolder(this.selectedFile.folder_resource_id)));
  }

  private loadFile(): void {
    if (this.isAccountScoped) {
      this.accountFoldersService
        .getFile(this.selectedFile.folder_resource_id, this.selectedFile.id)
        .pipe(
          untilComponentDestroyed(this),
          tap((fileResource) => this.applyFileResource(fileResource)),
          catchError((error) => {
            this.snackBar.open('Unable to open the file. Please try again later.', 'error');
            this.discard();
            return throwError(() => error);
          }),
        )
        .subscribe();

      return;
    }

    this.store
      .dispatch(new Folders.GetFileById(this.selectedFile.id))
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        const fileResource: FileResource = this.store.selectSnapshot((state) => state.folderState.selectedFile);

        if (fileResource) {
          this.applyFileResource(fileResource);
        }
      });
  }
}
